const PAYMENT_ACCOUNT_PATH_FIELD_NAME = "__paymentCustomerAccountPath";

/**
 * Returns contact fields in the desired order with safe fallbacks.
 */
function getOrderedContactFields(fields = [], order = []) {
    const availableFields = fields.filter(field => !!field);
    const orderList = Array.isArray(order) ? order.filter(id => !!id) : [];

    if (!orderList.length) return availableFields;

    const fieldMap = new Map();
    availableFields.forEach(field => {
        const id = field?.id || field?.name;
        if (id) fieldMap.set(id, field);
    });

    const ordered = [];
    const usedIds = new Set();
    orderList.forEach(id => {
        if (usedIds.has(id)) return;
        const field = fieldMap.get(id);
        if (field) {
            ordered.push(field);
            usedIds.add(id);
            fieldMap.delete(id);
        }
    });

    availableFields.forEach(field => {
        const id = field?.id || field?.name;
        if (!id || !usedIds.has(id)) ordered.push(field);
    });

    return ordered;
}

/**
 * Returns form spec fields used to capture customer data
 *
 * @param options {customer: boolean, matchBy: number}
 * @param disabled
 * @param honeyPotFieldName
 * @param context
 * @param step
 * @returns {object}
 */
function getSubmissionHandlingFields(
    options,
    disabled = false,
    honeyPotFieldName,
    context,
    step,
    includeSystemStep = true,
    customerSession,
    useAuthenticatedPaymentCustomer = false,
    paymentAllowsInlineRegistration,
    paymentCustomerAccountMode = 'required'
) {
    const {customer, matchBy, requriedFields, registerCustomer: configuredRegistration, contactFieldOrder} = options || {};
    const allowRegistration = useAuthenticatedPaymentCustomer && typeof paymentAllowsInlineRegistration === 'boolean'
        ? paymentAllowsInlineRegistration
        : configuredRegistration;
    const registerCustomer = allowRegistration && customerSession?.isAuthenticated !== true;
    const optionalPaymentRegistration = useAuthenticatedPaymentCustomer &&
        paymentCustomerAccountMode === 'optional' && registerCustomer;
    const registrationShowsWhen = optionalPaymentRegistration
        ? `${PAYMENT_ACCOUNT_PATH_FIELD_NAME} === 'register'`
        : undefined;
    const emailRequirued = (matchBy === 3 && requriedFields)
        ? (requriedFields === 1 || requriedFields === 3)
        : "customer.phoneNumbers[0].number === undefined || customer.phoneNumbers[0].number === null || customer.phoneNumbers[0].number === ''";

    const phoneRequirued = (matchBy === 3 && requriedFields)
        ? (requriedFields === 2 || requriedFields === 3)
        : matchBy === 2;

    const stepField = {
        system: true,
        type: "step",
        id: "customerStep",
        title: step?.title,
        text: step?.text,
        name: "customerStep"
    };

    // Payment endpoints bind the response to the credential-authenticated customer and replace submitted identity
    // data with that customer's canonical details. Avoid asking for the same name/email again in that narrow case.
    if (useAuthenticatedPaymentCustomer && customerSession?.isAuthenticated === true) return [];
    if (useAuthenticatedPaymentCustomer && paymentCustomerAccountMode === 'required' &&
        paymentAllowsInlineRegistration === false) return [];

    const contactFields = [
        customer ? {
            system: true,
            contactField: true,
            type: "control-group",
            id: "customer.nameGroup",
            fields: [
                {
                    system: true,
                    type: "text",
                    label: "First Name",
                    name: "customer.firstName",
                    maxLength: 150,
                    required: true,
                    errorMessage: "First name is required",
                    disabled,
                },
                {
                    system: true,
                    type: "text",
                    label: "Last Name",
                    name: "customer.lastName",
                    maxLength: 150,
                    disabled,
                },
            ],
        } : null,
        matchBy === 3 || matchBy === 1 ? {
            system: true,
            contactField: true,
            type: "email",
            autoComplete: "email",
            label: "Email",
            id: "customer.emailAddress",
            name: "customer.emailAddress",
            maxLength: 250,
            required: matchBy === 3 ? emailRequirued : true,
            errorMessage: "Email or phone number is required",
            disabled,
        } : null,
        matchBy === 3 || matchBy === 2 ? {
            system: true,
            contactField: true,
            type: "phone-number-input",
            label: "Contact Number",
            id: "customer.contactNumber",
            autoComplete: "tel",
            name: "customer.contactNumber",
            maxLength: 30,
            defaultCountry: context?.locale?.split('-')[1] ?? "AU",
            displayInitialValueAsLocalNumber: true,
            required: phoneRequirued,
            disabled,
        } : null,
        registerCustomer ? {
            system: true,
            contactField: true,
            type: "password",
            label: "Create Password",
            autoComplete: "new-password",
            name: "customer.password",
            canRevealSecret: true,
            id: "customer.password",
            showsWhen: registrationShowsWhen,
            minLength: 8,
            maxLength: 256,
            required: true,
            errorMessage: "Use at least 8 characters for your password",
            disabled,
        } : null
    ];

    const orderedContactFields = getOrderedContactFields(contactFields, contactFieldOrder);

    const fields = includeSystemStep ? [stepField, ...orderedContactFields] : orderedContactFields;
    return fields.filter(field => !!field);
}

module.exports = {getOrderedContactFields, getSubmissionHandlingFields, PAYMENT_ACCOUNT_PATH_FIELD_NAME};
